import type { Asset } from '@schibsted-svp/svp-api-types';
import { useEffect } from 'preact/hooks';

import { usePlayNext } from './usePlayNext';
import { usePlayerContainer } from './usePlayerContainer';
import { useViewable } from './useViewable';
import { useCountdown } from './useCountdown';

interface UseAutoplayNextCountdownProps {
  enabled: boolean;
  countdownInSeconds: number;
  nextAsset?: Asset;
  onCancelWhenHidden?: () => void;
  onBeforeAutoplayNext?: () => void;
}

export const useAutoplayNextCountdown = ({
  enabled,
  countdownInSeconds,
  nextAsset,
  onCancelWhenHidden,
  onBeforeAutoplayNext,
}: UseAutoplayNextCountdownProps) => {
  const { playNext } = usePlayNext();
  const playerContainer = usePlayerContainer();
  const { isViewable } = useViewable();
  const { remainingTime, resetCountdown, enableCountdown } = useCountdown(
    countdownInSeconds,
    'disabled'
  );

  useEffect(() => {
    if (!enabled) {
      resetCountdown('disabled');
      return;
    }

    if (isViewable) {
      enableCountdown();
    } else {
      resetCountdown('disabled');
    }
  }, [enabled, isViewable, enableCountdown, resetCountdown]);

  useEffect(() => {
    if (!enabled || remainingTime > 0 || !nextAsset) {
      return;
    }

    // Keep behavior aligned with regular end screen countdown.
    if (getComputedStyle(playerContainer).visibility === 'hidden') {
      onCancelWhenHidden?.();
    }

    onBeforeAutoplayNext?.();

    playNext(nextAsset, {
      pulse: { playbackSource: 'autoplayNext-countdown' },
    });
  }, [
    enabled,
    remainingTime,
    nextAsset,
    playerContainer,
    onCancelWhenHidden,
    onBeforeAutoplayNext,
    playNext,
  ]);

  return {
    remainingTime,
  };
};
